import { getApiBaseUrl } from '../utils/apiConfig';

// Helper to get auth headers (Supabase token)
async function getHeaders() {
  // Import supabase dynamically to avoid circular deps
  const { supabase } = await import('../lib/supabase');
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
}

// Cele mai multe funcții de tracking apelau hasSession() și apoi getHeaders()
// separat — fiecare cu propriul supabase.auth.getSession(), deci dublu lock
// Supabase per eveniment. getAuthState() face o singură citire de sesiune și
// scoate ambele informații din ea; comportamentul (silent fail, headers
// identice) e neschimbat.
async function getAuthState() {
  try {
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    return {
      hasSession: !!token,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    };
  } catch {
    return { hasSession: false, headers: { 'Content-Type': 'application/json' } };
  }
}

const API_BASE = getApiBaseUrl();
let currentSessionId = null;

// --- Listen Session ID ---
// Un UUID unic per sesiune de ascultare (play → stop/complete pe o meditație)
let _listenSessionId = null;
let _breathingSessionId = null;

export function getListenSessionId() {
  return _listenSessionId;
}

export function newListenSession() {
  _listenSessionId = crypto.randomUUID();
  return _listenSessionId;
}

export function clearListenSession() {
  _listenSessionId = null;
}

export function newBreathingSession() {
  _breathingSessionId = crypto.randomUUID();
  return _breathingSessionId;
}

export function getBreathingSessionId() {
  return _breathingSessionId;
}

export function clearBreathingSession() {
  _breathingSessionId = null;
}

// --- Play Events ---
export async function trackPlay(meditationId, event, { positionSec = 0, durationSec = 0, listenPercent = 0, isRepeat = false, listenedTotalSec = 0, listenSessionId = null } = {}) {
  try {
    const headers = await getHeaders();
    await fetch(`${API_BASE}/api/analytics/play`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ meditationId, event, positionSec, durationSec, listenPercent, isRepeat, listenSessionId: listenSessionId || _listenSessionId, listenedTotalSec })
    });
  } catch (e) { /* silent */ }
}

// --- Generic Events ---
export async function trackEvent(eventType, eventData = {}, screen = null) {
  try {
    const { hasSession, headers } = await getAuthState();
    if (!hasSession) return;
    await fetch(`${API_BASE}/api/analytics/event`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ eventType, eventData, screen: screen || getCurrentScreen() })
    });
  } catch (e) { /* silent */ }
}

export async function trackBreathingEvent(exerciseId, eventType, { sessionId = null, selectedDurationSec = null, elapsedSec = 0, completed = false, eventData = {} } = {}) {
  try {
    const { hasSession, headers } = await getAuthState();
    if (!hasSession) return;
    await fetch(`${API_BASE}/api/analytics/breathing`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        exerciseId,
        eventType,
        sessionId: sessionId || _breathingSessionId,
        selectedDurationSec,
        elapsedSec,
        completed,
        eventData,
      })
    });
  } catch (e) { /* silent */ }
}

// --- Session Management ---
export async function startSession(entryScreen = null) {
  try {
    const { hasSession, headers } = await getAuthState();
    if (!hasSession) return;
    const platform = detectPlatform();
    const res = await fetch(`${API_BASE}/api/analytics/session/start`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ platform, entryScreen: entryScreen || window.location.pathname })
    });
    const data = await res.json();
    currentSessionId = data.sessionId;
    sessionStartTime = Date.now();
  } catch (e) { /* silent */ }
}

let sessionStartTime = null;

export async function endSession() {
  if (!currentSessionId) return;
  try {
    const { hasSession, headers } = await getAuthState();
    if (!hasSession) { currentSessionId = null; sessionStartTime = null; return; }
    const durationSec = sessionStartTime ? Math.floor((Date.now() - sessionStartTime) / 1000) : 0;
    await fetch(`${API_BASE}/api/analytics/session/${currentSessionId}/end`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ durationSec })
    });
    currentSessionId = null;
    sessionStartTime = null;
  } catch (e) { /* silent */ }
}

// --- Helpers ---
function detectPlatform() {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'web';
}

function getCurrentScreen() {
  return window.location.pathname;
}

// --- Convenience wrappers ---
export function trackScreenView(screen) {
  trackEvent('screen_view', { screen }, screen);
}

export function trackWallHit(meditationId, source = 'player') {
  trackEvent('wall_hit', { meditation_id: meditationId, source });
}

export function trackFavorite(meditationId, action = 'add') {
  trackEvent(action === 'add' ? 'favorite_add' : 'favorite_remove', { meditation_id: meditationId });
}

export function trackSearch(query, resultsCount = 0) {
  trackEvent('search', { query, results_count: resultsCount });
}

export function trackFilterUse(filter, value) {
  trackEvent('filter_use', { filter, value });
}

export function trackCategoryClick(categoryId, title) {
  trackEvent('category_click', { category_id: categoryId, title });
}

export function trackPremiumCtaClick(source) {
  trackEvent('premium_cta_click', { source });
}

// Funnel premium:
//   wall_hit (vede paywall-ul)
//   → wall_checkout_click (apasa CTA-ul nostru)
//   → checkout_start (paywall-ul real Google/Apple/Stripe se deschide)
//   → checkout_cancel | checkout_failed | checkout_success
export function trackWallCheckoutClick(plan, source = 'premium_page') {
  trackEvent('wall_checkout_click', { plan, source });
}

export function trackCheckoutStart(plan, source = 'premium_page', platform = null) {
  trackEvent('checkout_start', {
    plan,
    source,
    platform: platform || detectPlatform(),
  });
}

export function trackCheckoutCancel(plan, source = 'premium_page', platform = null) {
  trackEvent('checkout_cancel', {
    plan,
    source,
    platform: platform || detectPlatform(),
  });
}

export function trackCheckoutFailed(plan, source = 'premium_page', reason = null, platform = null) {
  trackEvent('checkout_failed', {
    plan,
    source,
    reason: reason || 'unknown',
    platform: platform || detectPlatform(),
  });
}

// Dedupe `meditation_opened`: acelasi id nu se mai emite daca a fost deja
// raportat in ultimele DEDUPE_MS (remount rapid, React Strict Mode, navigare
// inapoi-inainte). In-memory, per sesiune.
const _openedRecent = new Map();
const MEDITATION_OPENED_DEDUPE_MS = 30 * 1000;

export function trackMeditationOpened(meditationId, source = 'direct') {
  if (!meditationId) return;
  const key = String(meditationId);
  const now = Date.now();
  const last = _openedRecent.get(key) || 0;
  if (now - last < MEDITATION_OPENED_DEDUPE_MS) return;
  _openedRecent.set(key, now);
  trackEvent('meditation_opened', { meditation_id: key, source });
}

export function trackPushOpen(notificationId) {
  trackEvent('push_open', { notification_id: notificationId });
}

export function trackOnboardingStep(step, selection = null) {
  trackEvent('onboarding_step', { step, ...(selection ? { selection } : {}) });
}

export function trackOnboardingComplete(selections) {
  trackEvent('onboarding_complete', { selections });
}
